import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PropTypes from 'prop-types';
import useThemeStyles from '../../../hooks/useThemeStyles';
import urls from '../../../urls';
import { getProjectTypeConfig } from '../../../utils/projectTypeConfig';
import { PROJECT_STATUS_LABELS } from '../../../utils/dashboard/filterProjects';
import formatLastEdited from '../../../utils/dashboard/formatLastEdited';

/**
 * Compact single-line project row for the dashboard's list view — the dense
 * counterpart to ProjectCard for users with many projects.
 */
const ProjectListRow = ({ project }) => {
  const navigate = useNavigate();
  const { colors, isDark } = useThemeStyles();

  const typeConfig = getProjectTypeConfig(project.type);
  const TypeIcon = typeConfig.icon;
  const status = project.status || 'draft';
  const statusLabel = PROJECT_STATUS_LABELS[status] || status;
  const lastEdited = formatLastEdited(project.updatedAt);
  const wordCount = typeof project.wordCount === 'number' ? project.wordCount : 0;
  const sharedRole = project.myRole && project.myRole !== 'owner' ? project.myRole : null;
  const goalWordCount =
    typeof project.goalWordCount === 'number' && project.goalWordCount > 0
      ? project.goalWordCount
      : null;
  const goalProgress = goalWordCount ? Math.min(1, wordCount / goalWordCount) : null;

  const openProject = () => {
    navigate(urls.projectDetails.replace(':projectId', project._id));
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={openProject}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openProject();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        width: '100%',
        px: 3,
        py: 2.25,
        borderRadius: '10px',
        cursor: 'pointer',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(20, 30, 47, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.95) 100%)',
        transition: 'border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease',
        '&:hover, &:focus-visible': {
          outline: 'none',
          transform: 'translateY(-1px)',
          borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)',
          boxShadow: isDark ? '0 6px 16px rgba(0, 0, 0, 0.35)' : '0 4px 12px rgba(0, 0, 0, 0.10)',
          '& .project-row-chevron': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      {/* Type icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: '8px',
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <TypeIcon sx={{ fontSize: '1.125rem', color: colors.text.secondary }} />
      </Box>

      {/* Title + description */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          sx={{
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: 500,
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontStyle: 'italic',
            letterSpacing: '-0.01em',
            lineHeight: 1.4,
          }}
        >
          {project.title || 'Untitled Project'}
        </Typography>
        <Typography
          noWrap
          sx={{
            color: colors.text.muted,
            fontSize: '0.75rem',
            lineHeight: 1.5,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {[typeConfig.label, (project.description || '').trim()].filter(Boolean).join(' — ')}
        </Typography>
        {goalProgress !== null && (
          <Box
            aria-label={`Goal progress: ${Math.round(goalProgress * 100)}%`}
            sx={{
              mt: 0.75,
              height: 3,
              maxWidth: 220,
              borderRadius: 2,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Box
              sx={{
                width: `${goalProgress * 100}%`,
                height: '100%',
                borderRadius: 2,
                backgroundColor: goalProgress >= 1 ? '#22c55e' : colors.interactive.primary,
              }}
            />
          </Box>
        )}
      </Box>

      {/* Shared badge */}
      {sharedRole && (
        <Box
          sx={{
            flexShrink: 0,
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.35)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontSize: '0.65rem',
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
            }}
          >
            Shared • {sharedRole}
          </Typography>
        </Box>
      )}

      {/* Metadata columns */}
      <Typography
        sx={{
          color: colors.text.muted,
          fontSize: '0.75rem',
          width: 90,
          textAlign: 'right',
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
        }}
      >
        {wordCount > 0 ? `${wordCount.toLocaleString()} words` : '—'}
      </Typography>
      <Typography
        sx={{
          color: colors.text.muted,
          fontSize: '0.75rem',
          width: 100,
          textAlign: 'right',
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {lastEdited}
      </Typography>

      {/* Status pill */}
      <Box
        sx={{
          flexShrink: 0,
          px: 1.25,
          py: 0.25,
          borderRadius: '4px',
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: colors.text.muted, fontSize: '0.65rem', whiteSpace: 'nowrap' }}
        >
          {statusLabel}
        </Typography>
      </Box>

      <ChevronRightIcon
        className="project-row-chevron"
        sx={{
          fontSize: '1.125rem',
          color: colors.text.muted,
          flexShrink: 0,
          opacity: 0,
          transform: 'translateX(-4px)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      />
    </Box>
  );
};

ProjectListRow.propTypes = {
  project: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    wordCount: PropTypes.number,
    goalWordCount: PropTypes.number,
    myRole: PropTypes.string,
    updatedAt: PropTypes.string,
  }).isRequired,
};

export default ProjectListRow;
